import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { SectionHeading } from './SectionHeading';

/* At-a-glance figures beside the portrait. The years badge used to be the only
   number on the section; three of them turn the block into something an
   officer can scan rather than read. */
const FACTS = [
  { icon: 'ri-time-line', value: '100+', labelKey: 'about.yearsOfPlanning' },
  { icon: 'ri-building-2-line', value: '14', label: 'Urban Development Authorities' },
  { icon: 'ri-map-2-line', value: '26', label: 'Districts Covered' },
];

export const AboutDTCP: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal({ threshold: 0.15 });

  return (
    <section
      id="about"
      ref={ref as React.RefObject<HTMLElement>}
      className={`scroll-reveal portal-section portal-surface relative overflow-hidden ${isRevealed ? 'revealed' : ''}`}
    >
      {/* Ambient accent bloom — depth without another background colour */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-24 w-[30rem] h-[30rem] rounded-full blur-3xl opacity-[0.07]"
        style={{ background: 'var(--portal-accent)' }}
      />

      <div className="reveal-content portal-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Image */}
          <div className="relative group">
            <div
              className="w-full overflow-hidden rounded-3xl shadow-[0_20px_50px_-16px_rgba(0,0,0,0.35)] transition-all duration-500 group-hover:shadow-[0_28px_64px_-18px_rgba(0,0,0,0.42)]"
              style={{ minHeight: '440px' }}
            >
              <img
                src="/images/about/about-meeting.jpeg"
                alt="About DTCP AP"
                className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                style={{ minHeight: '440px' }}
                loading="lazy"
              />
              <div
                className="absolute inset-0 rounded-3xl pointer-events-none"
                style={{ background: 'linear-gradient(to top, rgba(9,12,18,0.35), rgba(0,0,0,0) 55%)' }}
              />
            </div>

            {/* Years badge */}
            <div
              className="absolute -bottom-6 -right-4 text-white px-6 py-4 hidden lg:block rounded-2xl transition-transform duration-300 group-hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, var(--portal-accent), var(--portal-accent-light))',
                boxShadow: '0 16px 36px -10px rgba(0,0,0,0.45)',
              }}
            >
              <p className="text-[34px] font-extrabold leading-none tracking-tight">100+</p>
              <p className="text-[11px] text-white/90 font-semibold mt-1.5 tracking-[0.08em] uppercase">
                {t('about.yearsOfPlanning')}
              </p>
            </div>
          </div>

          {/* Right: Content */}
          <div>
            <SectionHeading
              eyebrow={t('about.label')}
              title={t('about.title')}
              className="!mb-6"
            />

            <p className="text-[15px] text-[#4B5563] dark:text-white/70 leading-[1.85] mb-5" dangerouslySetInnerHTML={{ __html: t('about.description1') }} />
            <p className="text-[15px] text-[#4B5563] dark:text-white/70 leading-[1.85] mb-8">
              {t('about.description2')}
            </p>

            {/* Key figures */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              {FACTS.map((f) => (
                <div
                  key={f.value + (f.label || f.labelKey || '')}
                  className="rounded-2xl border border-black/[0.07] dark:border-white/10 bg-white dark:bg-white/[0.04] px-3 py-4 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_30px_-12px_rgba(0,0,0,0.25)]"
                >
                  <i
                    className={`${f.icon} text-[19px]`}
                    style={{ color: 'var(--portal-accent)' }}
                  />
                  <div className="mt-1 text-[21px] font-extrabold leading-none text-[#161A22] dark:text-white tabular-nums">
                    {f.value}
                  </div>
                  <div className="mt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[#7A8493] leading-tight">
                    {f.labelKey ? t(f.labelKey) : f.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Mission / Vision */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                { icon: 'ri-focus-3-line', titleKey: 'about.mission', textKey: 'about.missionText' },
                { icon: 'ri-eye-line', titleKey: 'about.vision', textKey: 'about.visionText' },
              ].map((item) => (
                <div
                  key={item.titleKey}
                  className="
                    group relative overflow-hidden bg-white dark:bg-white/[0.04]
                    border border-black/[0.07] dark:border-white/10
                    rounded-2xl p-5 transition-all duration-300
                    hover:-translate-y-1 hover:shadow-[0_16px_34px_-14px_rgba(0,0,0,0.3)]
                  "
                >
                  {/* Left accent rail — replaces the hard 4px border, which
                      squared off one corner of an otherwise rounded card. */}
                  <span
                    aria-hidden="true"
                    className="absolute left-0 inset-y-3 w-[3px] rounded-full transition-all duration-300 group-hover:inset-y-0"
                    style={{ background: 'linear-gradient(180deg, var(--portal-accent), var(--portal-accent-light))' }}
                  />
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                      style={{ background: 'var(--portal-accent-soft)' }}
                    >
                      <i className={`${item.icon} text-lg`} style={{ color: 'var(--portal-accent)' }} />
                    </div>
                    <h4 className="font-bold text-[14.5px] text-[#161A22] dark:text-white">{t(item.titleKey)}</h4>
                  </div>
                  <p className="text-[13px] text-[#5B6472] dark:text-white/60 leading-[1.7]">{t(item.textKey)}</p>
                </div>
              ))}
            </div>

            <a
              href="/about"
              className="
                group relative inline-flex items-center gap-2 overflow-hidden text-white
                px-7 py-3.5 text-sm font-semibold rounded-full
                transition-all duration-300 cursor-pointer whitespace-nowrap
                hover:-translate-y-0.5
              "
              style={{
                background: 'linear-gradient(135deg, var(--portal-accent), var(--portal-accent-dark))',
                boxShadow: '0 12px 28px -10px var(--portal-accent)',
              }}
            >
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
              <span className="relative">{t('about.cta')}</span>
              <i className="relative ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
